/*
Yapo Form Component for Publication Manager
Integrated form with nested tabs for vehicle details and images (no video - Yapo doesn't support it)
Autor: Assistant
Fecha: 2025-01-23
*/
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Button } from '@radix-ui/themes'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import Vehicle from '../interfaces/Vehicle'
import Brand from '../interfaces/Brand'
import useYapoFetcher from '../hooks/useYapoFetcher'
import YapoModel from '../interfaces/YapoModel'
import { useAuth } from '../hooks/useAuth'
import useYapoPublisher from '../hooks/useYapoPublisher'
import useBranchesFetcher from '../hooks/useBranchesFetcher'
import useLocalization from '../hooks/useLocalization'
import { validateFields, Rule } from '../utils/validateForm'
import { BranchDto } from '../interfaces/Branch'
import Region from '../interfaces/Region'
import VehicleBody from '../interfaces/VehicleBody'
import usePublicationDataSaver from '../hooks/usePublicationDataSaver'
import useFileUpload, { UserOrganization } from '../hooks/useFileUpload'
import useImagesReorder from '../hooks/useImagesReorder'
import useImageSelection from '../hooks/useImageSelection'
import useImageBulkDownload from '../hooks/useImageBulkDownload'
import { makeSmallImage } from '../utils/makeImage'
import YapoInputVehicle from '../interfaces/YapoInputVehicle'

import ConfirmationModal from './ConfirmationModal'
import DisplayError from './DisplayError'
import ImageSelectionToolbar from './ImageSelectionToolbar'
import ImageLightbox from './ImageLightbox'
import { Info } from './MessageInfo'
import InputField from './InputField'
import { SelectField } from './SelectField'

interface YapoData {
  type: string
  body: string
  brand: string
  model: string
  year: number
  fuel: string
  gearbox: string
  comments: string
  localization: string
}

interface YapoFormProps {
  vehicle: Vehicle
  branch: BranchDto | null
  onPublishSuccess: () => void
  onImageUpload?: (vehicleId: string) => void
}

const SortableItem = ({
  id,
  selectionMode = false,
  isSelected = false,
  onSelectionChange,
  onImageClick,
}: {
  id: string
  selectionMode?: boolean
  isSelected?: boolean
  onSelectionChange?: (id: string) => void
  onImageClick?: () => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('Yapo - Image clicked, selectionMode:', selectionMode, 'onImageClick exists:', !!onImageClick)

    if (!selectionMode && onImageClick) {
      console.log('Yapo - Calling onImageClick')
      onImageClick()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 flex items-center justify-center rounded-md border bg-white p-2 shadow-sm relative ${
        selectionMode && isSelected ? 'ring-2 ring-green-500' : ''
      }`}
    >
      {/* Drag handle - only visible when not in selection mode */}
      {!selectionMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 z-10 bg-gray-700/80 hover:bg-gray-600/80 text-white rounded p-1 cursor-grab active:cursor-grabbing"
          title="Arrastrar para reordenar"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 3h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm4-12h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
          </svg>
        </div>
      )}

      {/* Selection checkbox */}
      {selectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 bg-white shadow-sm focus:ring-2 focus:ring-green-500"
            checked={isSelected}
            onChange={() => onSelectionChange?.(id)}
          />
        </div>
      )}

      <img
        src={makeSmallImage(id)}
        alt="Vehicle"
        className="h-[256px] w-[247px] rounded-md object-cover cursor-pointer transition-opacity duration-200 hover:opacity-90"
        onClick={handleImageClick}
        title="Clic para ver en pantalla completa"
      />
    </div>
  )
}

const YapoForm: React.FC<YapoFormProps> = ({ vehicle, branch, onPublishSuccess, onImageUpload }) => {
  const { userData } = useAuth()
  const [activeTab, setActiveTab] = useState<'details' | 'images'>('details') // No video tab for Yapo

  // Form data state (same as Yapo.tsx)
  const [formData, setFormData] = useState<YapoData>({
    type: vehicle.type,
    body: vehicle.body,
    brand: vehicle.brand,
    model: vehicle.model,
    year: vehicle.year,
    fuel: vehicle.fuel,
    gearbox: vehicle.gearbox,
    // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
    comments: vehicle.promo?.message || ' ',
    localization: '',
  })

  // Data fetching hooks
  const { yapoBrands, yapoTypes, yapoModels, yapoBodies, yapoYears, yapoFuel, yapoGearbox } = useYapoFetcher()

  // Publishing hooks
  const { yapoPublish, yapoSetPublished } = useYapoPublisher()
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  // State for dropdowns (same as Yapo.tsx)
  const [brands, setBrands] = useState<Brand[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [bodies, setBodies] = useState<VehicleBody[]>([])
  const [years, setYears] = useState<number[]>([])
  const [fuel, setFuel] = useState<Record<string, string>>({})
  const [gearbox, setGearbox] = useState<Record<string, string>>({})

  // Image management
  const [publicImages, setPublicImages] = useState<string[]>(vehicle.pictures?.public ?? [])
  const { imagesReorder } = useImagesReorder()
  const [isOrderSaveDisabled, setOrderSaveDisabled] = useState<boolean>(true)

  // Bulk download and delete functionality
  const { downloadMultipleImages, downloadProgress, deleteMultipleImages, deleteProgress } = useImageBulkDownload()

  // State for delete confirmation
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false)
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

  // Bulk delete handler with confirmation
  const handleBulkDeleteRequest = useCallback((imageUrls: string[]) => {
    setImagesToDelete(imageUrls)
    setDeleteConfirmationOpen(true)
  }, [])

  // Image selection functionality (defined before handlers that use it)
  const imageSelection = useImageSelection({
    images: publicImages,
    onBulkDownload: downloadMultipleImages,
    onBulkDelete: handleBulkDeleteRequest,
  })

  const handleBulkDeleteConfirm = useCallback(async () => {
    setDeleteConfirmationOpen(false)
    const result = await deleteMultipleImages(imagesToDelete, vehicle._id, false, (deletedImageUrl) => {
      // Update local state immediately for each successful deletion
      setPublicImages((prevImages) => prevImages.filter((url) => url !== deletedImageUrl))
    })

    if (result && result.success > 0) {
      // Clear selection after successful deletions
      imageSelection.resetSelection()

      // Trigger image upload callback to refresh parent state
      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }

    setImagesToDelete([])
  }, [imagesToDelete, deleteMultipleImages, vehicle._id, onImageUpload, imageSelection])

  const handleBulkDeleteCancel = useCallback(() => {
    setDeleteConfirmationOpen(false)
    setImagesToDelete([])
  }, [])

  // Lightbox functions
  const openImageInLightbox = (index: number) => {
    console.log('Yapo - Opening lightbox with index:', index, 'Total images:', publicImages.length)
    setLightboxInitialIndex(index)
    setIsLightboxOpen(true)
  }

  const closeLightbox = () => {
    console.log('Yapo - Closing lightbox')
    setIsLightboxOpen(false)
  }

  // UI state (same as Yapo.tsx)
  const [allBrands, setAllBrands] = useState<Brand[]>([])
  const [allBodies, setAllBodies] = useState<VehicleBody[]>([])
  const [models, setModels] = useState<YapoModel[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const [branchRegionId, setBranchRegionId] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [faltaMsg, setFaltaMsg] = useState<string>('')
  const [saveMessage, setSaveMessage] = useState<string>('')
  const [hasUserSelectedRegion, setHasUserSelectedRegion] = useState<boolean>(false)

  // Notification system (Level 2 - Standard)
  const [publishMessage, setPublishMessage] = useState<string>('')
  const [publishTrigger, setPublishTrigger] = useState<number>(0)
  const [isPublishing, setIsPublishing] = useState<boolean>(false)

  // Lightbox state for enhanced image viewing
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0)

  // Branch fetching hooks
  const { fetchBranchDetail } = useBranchesFetcher()
  const { fetchMunicipalities, fetchRegionDetail } = useLocalization()

  // Reference for handling localization loading
  const tempLocalizationRef = useRef<string | null>(null)

  const userOrganization: UserOrganization | null = userData
    ? {
        organization_id: vehicle.organization,
        user_id: userData?.user?._id ?? '',
        vehicle_id: vehicle._id,
        registry: '0',
        attachment: '0',
      }
    : null

  const { isUploading, handleFileChangeEx } = useFileUpload(userOrganization!, (newImageUrls?: string[]) => {
    if (newImageUrls && newImageUrls.length > 0) {
      setPublicImages((prevImages) => [...prevImages, ...newImageUrls])

      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  })

  // Individual data fetching functions (same as Yapo.tsx)
  const fetchBrandsAsync = async () => {
    const toutBrands = await yapoBrands()

    if (toutBrands.length > 0) {
      setAllBrands(toutBrands)
    }
  }

  const fetchTypesAsync = async () => {
    const allTypes = await yapoTypes()
    setTypes(allTypes)
  }

  const fetchBodiesAsync = async () => {
    const allBodies = await yapoBodies()
    setAllBodies(allBodies)
  }

  const fetchYearsAsync = async () => {
    const years = await yapoYears()
    setYears(years)
  }

  const fetchFuelAsync = async () => {
    const fuel = await yapoFuel()
    setFuel(fuel)
  }

  const fetchGearboxAsync = async () => {
    const data = await yapoGearbox()
    setGearbox(data)
  }

  const fetchBranchAsync = async () => {
    if (!vehicle.branch) {
      setFaltaMsg('Asigna una sucursal al vehículo.')
      setDisabled(true)

      return
    }

    const branchData = await fetchBranchDetail(vehicle.branch)

    if (!branchData) {
      setFaltaMsg('Sucursal no encontrada.')
      setDisabled(true)

      return
    }

    const comuna = branchData?.municipality_format ?? ''

    if (!comuna) {
      setFaltaMsg('Por favor, asigna una comuna a la sucursal.')
      setDisabled(true)

      return
    }

    // Fetch regions/municipalities
    const regs = await fetchMunicipalities()

    if (regs) {
      setRegions(regs)
      const region = regs.find((r) => r.name === comuna)

      if (region) {
        setBranchRegionId(region.regionId)
      }
    }
  }

  // Sequential loading implementation (same as Yapo.tsx)
  useEffect(() => {
    const loadAllData = async () => {
      await Promise.all([
        fetchBrandsAsync(),
        fetchTypesAsync(),
        fetchBodiesAsync(),
        fetchYearsAsync(),
        fetchFuelAsync(),
        fetchGearboxAsync(),
        fetchBranchAsync(),
      ])
    }

    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      // SEQUENTIAL LOADING: Load dependencies first, then draft data
      // Step 1: Load all external APIs and their data (regions, models, etc.)
      await loadAllData()

      // Step 2: Now that regions/models are loaded, apply draft data
      await autoLoadDraftData()

      setExternalApisComplete(true)
      setIsDraftLoading(false)
    }

    initializeComponentData()

    let sw = true

    if (!userData?.organization?.external?.yapo?.enabled) {
      setFaltaMsg('Su organización no está autorizada para publicar en Yapo.')
      sw = false
    } else if (!userData?.organization?.external?.yapo?.importApiKey) {
      setFaltaMsg("Falta la 'key' para publicar en Yapo.")
      sw = false
    } else if (!userData?.organization?.external?.yapo?.userId) {
      setFaltaMsg("Falta el 'userId' para publicar en Yapo.")
      sw = false
    }

    if (!sw) {
      setDisabled(true)
    }
  }, [])

  // Type-based brand filtering (same as Yapo.tsx)
  useEffect(() => {
    if (formData && allBrands) {
      const brands = allBrands.filter((b) => b.type === formData.type)
      setBrands(brands)
    }
  }, [formData.type, allBrands])

  // Type-based body filtering (same as Yapo.tsx)
  useEffect(() => {
    const bodiesPerType = allBodies.filter((b) => b.type === formData.type)
    setBodies(bodiesPerType)
  }, [formData.type, allBodies])

  // Convert full enum type to simplified type for Yapo API (same as Yapo.tsx)
  const getYapoType = (fullType: string): string => {
    const typeMapping: Record<string, string> = {
      'TYPES.VEHICLE.AUTOMOBILE': 'auto',
      'TYPES.VEHICLE.MOTORCYCLE': 'moto',
      'TYPES.VEHICLE.TRUCK': 'camion',
      'TYPES.VEHICLE.BUS': 'bus',
      'TYPES.VEHICLE.MACHINERY': 'maquinaria',
      'TYPES.VEHICLE.NAUTIC': 'nautico',
    }

    return typeMapping[fullType] || 'auto'
  }

  // Models effect - fetch models when brand/type changes (same as Yapo.tsx)
  useEffect(() => {
    const fetchModelsAsync = async () => {
      if (formData.brand) {
        const yapoType = getYapoType(formData.type)
        const models = await yapoModels(yapoType, formData.brand)
        setModels(models)

        // Si el modelo que viene en el vehiculo (formData) no está en la lista, entonces
        // lo limpiamos - BUT NOT during draft loading
        if (!isDraftLoading) {
          // Case-insensitive model search
          const hay = models.find((c) => c.label.toLowerCase() === formData.model?.toLowerCase())

          if (!hay && formData.model) {
            setFormData((prev) => ({ ...prev, model: '' }))
          }
        }
      } else {
        setModels([])
      }
    }
    fetchModelsAsync()
  }, [formData.brand, formData.type, isDraftLoading])

  // Handle region sync when regions are loaded and we have a pending localization (same as Yapo.tsx)
  useEffect(() => {
    if (regions.length > 0 && tempLocalizationRef.current) {
      const savedLocalization = tempLocalizationRef.current
      const region = regions.find((r) => r.name === savedLocalization)

      if (region) {
        setBranchRegionId(region.regionId)
        setFormData((prev) => ({ ...prev, localization: savedLocalization }))
      }
      tempLocalizationRef.current = null // Clear the reference
    }
  }, [regions])

  // Ensure formData.localization is always in sync with branchRegionId when regions are available
  useEffect(() => {
    if (regions.length > 0 && branchRegionId && !formData.localization) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        setFormData((prev) => ({ ...prev, localization: selectedRegion.name }))
      }
    }
  }, [regions, branchRegionId, formData.localization])

  // Auto-load draft data (same pattern as Yapo.tsx)
  const autoLoadDraftData = async () => {
    setIsDraftLoading(true)

    const result = await loadPublicationData<YapoData>(vehicle._id, 'yapo')

    if (result.success && result.data) {
      // Merge draft data with current form data, draft takes priority
      setFormData((prev) => ({ ...prev, ...result.data }))

      // Store localization value for later processing when regions are ready
      if (result.data.localization) {
        // We'll handle region sync in a useEffect that watches regions changes
        tempLocalizationRef.current = result.data.localization
      }

      // Wait a bit to ensure formData state update completes
      // before setting isDraftLoading to false (which triggers model validation)
      setTimeout(() => {
        setIsDraftLoading(false)
      }, 200)
    } else if (!result.success) {
      // Silently fail for auto-load - don't show error messages
      console.warn('Failed to auto-load draft:', result.message)
      setIsDraftLoading(false)
    } else {
      // No draft data exists
      setIsDraftLoading(false)
    }
  }

  // Draft saving function
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    // Ensure formData.localization is synchronized before saving
    let dataToSave = { ...formData }

    // If user hasn't explicitly selected a region but branchRegionId has a value,
    // sync the localization field
    if (!hasUserSelectedRegion && branchRegionId && regions.length > 0) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        dataToSave = { ...dataToSave, localization: selectedRegion.name }
      }
    }

    const result = await savePublicationData(vehicle._id, 'yapo', dataToSave as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Input handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear errors when user starts typing
    if (errors[name]) {
      const updatedErrors = { ...errors }
      delete updatedErrors[name]
      setErrors(updatedErrors)
    }
  }

  // Brand change handler (clears model)
  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      model: '', // Clear model when brand changes
    }))
  }

  // Type change handler (clears brand and model) - same as Yapo.tsx
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      brand: '', // Clear brand when type changes
      model: '', // Clear model when type changes
    }))
  }

  // Region change handler
  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target
    setBranchRegionId(value)
    setHasUserSelectedRegion(true) // Mark that user has explicitly selected a region

    // Find the region name from the regionId and save that to localization
    const selectedRegion = regions.find((r) => r.regionId === value)
    const regionName = selectedRegion ? selectedRegion.name : value
    setFormData((prev) => ({ ...prev, localization: regionName }))
  }

  // Image management
  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setPublicImages((items) => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        setOrderSaveDisabled(false)

        return newOrder
      })
    }
  }

  const handleSaveOrder = async () => {
    const resp = await imagesReorder(publicImages, vehicle._id, false)

    if (resp) {
      setOrderSaveDisabled(true)

      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (userOrganization) {
      await handleFileChangeEx(event)
    }
  }

  // Publishing logic
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    // Existing validation and publish logic (unchanged)
    if (!userData?.organization) {
      setPublishMessage('❌ Error: Usuario no autenticado')
      setPublishTrigger(Date.now())

      return
    }

    if (!branch) {
      setPublishMessage('❌ Error: No se encontró la sucursal')
      setPublishTrigger(Date.now())

      return
    }

    // Apply same validation as Yapo.tsx using translated values for validation
    const resolvedValues = {
      type: formData.type || vehicle.type_format,
      body: formData.body || vehicle.body_format,
      brand: formData.brand || vehicle.brand,
      model: formData.model || vehicle.model,
      fuel: formData.fuel || vehicle.fuel_format,
      gearbox: formData.gearbox || vehicle.gearbox_format,
      title: `${vehicle.year} ${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
    }

    const validationErrors = validateFields([
      { name: 'type', value: resolvedValues.type, rule: Rule.REQUIRED },
      { name: 'body', value: resolvedValues.body, rule: Rule.REQUIRED },
      { name: 'brand', value: resolvedValues.brand, rule: Rule.REQUIRED },
      { name: 'model', value: resolvedValues.model, rule: Rule.REQUIRED },
      { name: 'year', value: formData.year || vehicle.year, rule: Rule.REQUIRED },
      { name: 'fuel', value: resolvedValues.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: resolvedValues.gearbox, rule: Rule.REQUIRED },
    ])

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setPublishMessage(`❌ Error de validación: ${Object.values(validationErrors).join(', ')}`)
      setPublishTrigger(Date.now())

      return
    }

    // Check for pictures
    const pictures = publicImages.length > 0 ? publicImages : vehicle.pictures?.public || []

    if (pictures.length === 0) {
      setPublishMessage('❌ Error: El vehículo debe tener al menos una imagen pública')
      setPublishTrigger(Date.now())

      return
    }

    // Get region detail
    const region = await fetchRegionDetail(branchRegionId)

    if (!region) {
      setPublishMessage('❌ Error: No se encontró la región para Yapo')
      setPublishTrigger(Date.now())

      return
    }

    try {
      setIsPublishing(true)

      const yapoPayload: YapoInputVehicle = {
        ...vehicle,
        ...formData,
        organization: userData.organization,
        pictures: pictures,
        branch: { ...branch, yapoRegionId: region.yapoRegionId },
        characteristics: vehicle.characteristics || [],
      }

      const result = await yapoPublish(yapoPayload)

      if (result?.ok) {
        const respSetPublished = await yapoSetPublished(vehicle._id, userData?.profile?._id ?? '')

        if (respSetPublished) {
          setPublishMessage('✅ Vehículo guardado y publicado exitosamente en Yapo')
          setPublishTrigger(Date.now())
          onPublishSuccess()
        } else {
          setPublishMessage('❌ Error al marcar como publicado')
          setPublishTrigger(Date.now())
        }
      } else {
        setPublishMessage('❌ Error al publicar en Yapo')
        setPublishTrigger(Date.now())
      }
    } catch (error) {
      setPublishMessage(`❌ Error al publicar: ${error instanceof Error ? error.message : 'Error desconocido'}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsPublishing(false)
    }
  }

  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  const beforePublish = async () => {
    // Save draft data first before publishing
    console.log('Saving draft data before publishing to Yapo...')

    // Ensure formData.localization is synchronized before saving
    let dataToSave = { ...formData }

    // If user hasn't explicitly selected a region but branchRegionId has a value,
    // sync the localization field
    if (!hasUserSelectedRegion && branchRegionId && regions.length > 0) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        dataToSave = { ...dataToSave, localization: selectedRegion.name }
      }
    }

    const saveResult = await savePublicationData(vehicle._id, 'yapo', dataToSave as unknown as Record<string, unknown>)

    if (!saveResult.success) {
      setPublishMessage(`❌ Error al guardar datos: ${saveResult.message}`)
      setPublishTrigger(Date.now())

      return
    }

    console.log('✅ Draft data saved successfully, proceeding with Yapo publication')

    // Directly call publish logic (restoring original behavior - no modal)
    await handlePublishConfirm()
  }

  if (!userOrganization) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-4">
      {/* Nested Tab Navigation for Yapo (no Video tab) */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Detalles del Vehículo
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'images'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Imágenes Públicas ({publicImages.length})
          </button>
          {/* Note: No Video tab for Yapo as requested */}
        </nav>
      </div>

      {/* Error/Fault Message Display */}
      {faltaMsg && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700 text-sm">{faltaMsg}</div>
        </div>
      )}

      {/* Tab Content */}
      <div>
        {activeTab === 'details' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Detalles del Vehículo - Yapo</h3>

            {/* Show loading message (same as vehicle detail page) */}
            {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
              !(saveMessage && saveMessage.startsWith('✅')) && (
                <div className="flex justify-center mt-2">
                  <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
                </div>
              )}

            {!isDraftLoading && minimumTimeElapsed && externalApisComplete && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <SelectField
                  label="Tipo"
                  name="type"
                  value={formData.type}
                  options={types}
                  handleInputChange={handleTypeChange}
                  errors={errors}
                />
                <SelectField
                  label="Carrocería"
                  name="body"
                  value={formData.body}
                  options={bodies.reduce((acc, cur) => ({ ...acc, [cur.body]: cur.description }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Marca"
                  name="brand"
                  value={formData.brand}
                  options={brands.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.name }), {})}
                  handleInputChange={handleBrandChange}
                  errors={errors}
                />
                <SelectField
                  label="Modelo"
                  name="model"
                  value={formData.model}
                  options={models.reduce((acc, cur) => ({ ...acc, [cur.val]: cur.label }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Año"
                  name="year"
                  value={formData.year.toString()}
                  options={years.reduce((acc, cur) => ({ ...acc, [cur]: cur.toString() }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Combustible"
                  name="fuel"
                  value={formData.fuel}
                  options={fuel}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Transmisión"
                  name="gearbox"
                  value={formData.gearbox}
                  options={gearbox}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Región"
                  name="localization"
                  value={branchRegionId}
                  options={regions.reduce((acc, cur) => ({ ...acc, [cur.regionId]: cur.name }), {})}
                  handleInputChange={handleRegionChange}
                  errors={errors}
                />
                <div className="sm:col-span-2 lg:col-span-3">
                  <InputField
                    label="Descripción"
                    name="comments"
                    value={formData.comments}
                    type="textarea"
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'images' && (
          <div className="space-y-4">
            {/* Bulk Selection Toolbar */}
            {publicImages.length > 0 && (
              <ImageSelectionToolbar
                isSelectionMode={imageSelection.isSelectionMode}
                selectedCount={imageSelection.selectedCount}
                totalCount={imageSelection.totalCount}
                onToggleSelectionMode={imageSelection.toggleSelectionMode}
                onSelectAll={imageSelection.selectAll}
                onSelectNone={imageSelection.selectNone}
                onBulkDownload={imageSelection.handleBulkDownload}
                canDownload={imageSelection.canDownload}
                downloadProgress={downloadProgress}
                onBulkDelete={imageSelection.handleBulkDelete}
                canDelete={imageSelection.canDelete}
                showDeleteOption={true}
                deleteProgress={deleteProgress}
              />
            )}

            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Imágenes Públicas - Yapo</h3>
              {!imageSelection.isSelectionMode && (
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="yapo-file-upload"
                    className={`cursor-pointer rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 ${isUploading && 'cursor-not-allowed opacity-50'}`}
                  >
                    {isUploading ? 'Cargando...' : 'Agregar imagen...'}
                  </label>
                  <input
                    id="yapo-file-upload"
                    type="file"
                    multiple={true}
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button onClick={handleSaveOrder} disabled={isOrderSaveDisabled}>
                    Guardar orden
                  </Button>
                </div>
              )}
            </div>

            {/* Enhanced Grid Layout with Lightbox Integration */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={publicImages} strategy={verticalListSortingStrategy}>
                  {publicImages.map((id, index) => (
                    <div key={id} className="relative">
                      <SortableItem
                        id={id}
                        selectionMode={imageSelection.isSelectionMode}
                        isSelected={imageSelection.isImageSelected(id)}
                        onSelectionChange={imageSelection.handleSelectionChange}
                        onImageClick={() => openImageInLightbox(index)}
                      />
                    </div>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            {publicImages.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No hay imágenes públicas. Agregue algunas para mejorar la publicación.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save message area (same as vehicle detail page) */}
      {(saveMessage || saveError) && (
        <div className="flex justify-center mt-2">
          <div className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMessage || saveError}
          </div>
        </div>
      )}

      {/* Notification display */}
      {publishMessage && (
        <div className="flex justify-center mt-4">
          <Info
            message={publishMessage}
            trigger={publishTrigger}
            onTimeout={() => setPublishMessage('')}
            noCenter={true}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-center gap-4">
        <Button
          onClick={handleSaveDraft}
          disabled={savingLoading}
          className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
        >
          {savingLoading ? 'Guardando...' : 'Guardar Borrador'}
        </Button>
        <Button
          onClick={beforePublish}
          disabled={disabled || isPublishing}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPublishing ? 'Publicando...' : 'Publicar en Yapo'}
        </Button>
      </div>

      {/* Error Display */}
      {Object.keys(errors).length > 0 && <DisplayError msg={Object.values(errors).join(', ')} />}

      {/* Confirmation Modal */}
      {confirmationModalOpen && (
        <ConfirmationModal
          message="¿Está seguro de que desea publicar este vehículo en Yapo?"
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <ConfirmationModal
          message={`¿Estás seguro de eliminar ${imagesToDelete.length} imagen${imagesToDelete.length > 1 ? 'es' : ''}? Esta acción no se puede deshacer.`}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={handleBulkDeleteCancel}
        />
      )}

      {/* Enhanced Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        imageUrls={publicImages}
        initialIndex={lightboxInitialIndex}
        onDelete={(imageUrl) => {
          // Remove from local state immediately
          setPublicImages((prevImages) => prevImages.filter((url) => url !== imageUrl))

          // Trigger data refresh
          if (onImageUpload) {
            onImageUpload(vehicle._id)
          }
        }}
        canDelete={true}
      />
    </div>
  )
}

export default YapoForm
